import { useNavigate } from "react-router";
import PageHeader from "../../components/PageHeader";
import { ReactComponent as ShieldCheck } from "../../assets/icons/shield-check-1.svg";
import { ReactComponent as Chevron } from "../../assets/icons/chevron-down.svg";

import {
  damageBadgeVariant,
  getDamageLevel,
  getHighestDamage,
  getRepairType,
  stringValue,
} from "../../utils/helper";
import { DAMAGE_LEVEL_MN } from "../../utils/constant";
import Typography from "../../base-components/Typography";
import IconButton from "../../base-components/IconButton";
import Badge from "../../base-components/Badge";
import Tooltip from "../../base-components/Tooltip";
import { ReactComponent as Question } from "../../assets/icons/question.svg";
import SliderLevel from "../../base-components/Slider";
import Empty from "../../base-components/Empty";
import MuiAccordion, {
  MuiAccordionDetails,
  MuiAccordionHeader,
} from "../../base-components/Accordion";
import Button from "../../base-components/Button";
import Card from "../../components/Card";
import useStore2 from "../../store/hook";
import { iZuraguData } from "../../store/zuraguSlice";

const getDetectedDamages = (data: iZuraguData[] | string | null) =>
  Array.isArray(data) ? data : [];

const Result = () => {
  const navigate = useNavigate();

  const zuragu = useStore2.useZuragu();
  const vehicleInfo = useStore2.useVehicleInfo();

  const hasDamages = zuragu.some(
    (zur) => Array.isArray(zur.data) && zur.data.length > 0
  );

  const renderDamages = () => {
    let index = 0;
    if (zuragu.length > 0) {
      const renderedDamages = zuragu.map((zur, idx) => {
        const damages = getDetectedDamages(zur.data);
        if (damages.length > 0) {
          return damages.map((da, data_index) => {
            index += 1;
            return (
              <MuiAccordion key={`damaged-part-${idx}-${data_index}`}>
                <MuiAccordionHeader
                  expandIcon={
                    <IconButton size="sm" variant="primary-soft">
                      <Chevron className="h-[18px] w-[18px]" />
                    </IconButton>
                  }
                >
                  <div className="flex items-center space-x-3">
                    <div className="inline-flex justify-center items-center h-10 w-10 rounded-full bg-secondary-12">
                      <ShieldCheck fill="#E3A13A" />
                    </div>
                    <div className="grow">
                      <Typography variant="Title" size="sm">
                        Гэмтэл - {index}
                      </Typography>
                      <Typography size="md" className="text-black-64">
                        {stringValue(
                          da.part_class.charAt(0).toUpperCase() +
                            da.part_class.slice(1).toLowerCase()
                        )}
                      </Typography>
                    </div>
                  </div>
                </MuiAccordionHeader>
                <MuiAccordionDetails>
                  <div className="rounded-lg mb-3 flex justify-center">
                    <img
                      src={
                        da.image_base64
                          ? `data:image/png;base64,${da.image_base64}`
                          : "/images/damage.png"
                      }
                      alt="damage"
                      className="max-w-full h-auto"
                    />
                  </div>
                  <div className="border border-dark-12 rounded-lg bg-white py-2.5 px-4">
                    <div className="flex items-center space-x-2">
                      <Typography size="sm" className="text-black-64">
                        Эвдрэлийн зэрэг:
                      </Typography>
                      <Badge variant={damageBadgeVariant(getDamageLevel(da.damage_class))}>
                        {DAMAGE_LEVEL_MN[getDamageLevel(da.damage_class)]}
                      </Badge>
                      <Tooltip label="Бага: бага засвар. Дунд: сэлбэг солих. Их: томоохон засвар">
                        <button
                          type="button"
                          aria-label="Эвдрэлийн зэргийн тайлбар"
                          className="text-black-24 outline-none focus-visible:shadow-focus rounded-full"
                        >
                          <Question className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                    <SliderLevel level={getDamageLevel(da.damage_class)} />
                    <div className="grid grid-cols-2 my-2">
                      <div>
                        <Typography size="sm" className="text-black-64">
                          Эвдрэлийн төрөл:
                        </Typography>
                        <Typography variant="Title" size="sm">
                          {getHighestDamage(da.damage_class)
                            .charAt(0)
                            .toUpperCase() +
                            getHighestDamage(da.damage_class)
                              .slice(1)
                              .toLowerCase()}
                        </Typography>
                      </div>
                      <div>
                        <Typography size="sm" className="text-black-64">
                          Засах/Солих:
                        </Typography>
                        <Typography variant="Title" size="sm">
                          {getRepairType(da.damage_class)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </MuiAccordionDetails>
              </MuiAccordion>
            );
          });
        } else {
          return "";
        }
      });
      return index > 0 ? (
        renderedDamages
      ) : (
        <Empty
          className="py-8"
          value="Парт болон эвдрэл танигдаагүй байна"
        />
      );
    } else {
      return;
    }
  };

  return (
    <div>
      <PageHeader title="Үнэлгээ хийгдэх зураг" backTo="/damages" />
      <Card>
        <section className="rounded-lg bg-primary relative flex justify-between py-4 px-3">
          <div>
            <Typography size="sm" className="text-white/[64%]">
              Улсын дугаар:
            </Typography>
            <Typography variant="Title" size="sm" className="text-white">
              {stringValue(vehicleInfo?.plateNumber) || "—"}
            </Typography>
          </div>
          <div>
            <Typography size="sm" className="text-white/[64%]">
              Үйлдвэрлэгч, Марк:
            </Typography>
            <Typography variant="Title" size="sm" className="text-white">
              {[vehicleInfo?.make, vehicleInfo?.model].filter(Boolean).join(" ") ||
                "—"}
            </Typography>
          </div>
        </section>
        <section className="my-2 space-y-2 divide-y divide-dark-12">
          {renderDamages()}
        </section>
      </Card>
      {hasDamages ? (
        <Button
          className="w-full"
          onClick={() => {
            navigate("/calculations");
          }}
        >
          Үнэлгээ хийлгэх
        </Button>
      ) : (
        <div className="flex flex-col items-center gap-4 py-2">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success-soft">
            <ShieldCheck fill="#0E8C4A" className="h-7 w-7" />
          </div>
          <div className="text-center space-y-1">
            <Typography variant="Title" size="md">
              Сайн мэдээ!
            </Typography>
            <Typography className="text-black-64">
              Зурган дээрээс эвдрэл олдоогүй тул үнэлгээ хийх шаардлагагүй.
            </Typography>
          </div>
          <Button
            variant="primary-outlined"
            className="w-full"
            onClick={() => navigate("/damages")}
          >
            Зураг дахин авах
          </Button>
        </div>
      )}
    </div>
  );
};

export default Result;
